import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { englishError } from "../utils/databaseErrors";
import { businessDate } from "../utils/stock";
import { journalAmounts, journalDate, journalDayBounds, journalPrintRows, loadDailyJournal } from "../utils/dailyJournal";
import { escapeHtml, money, PAYMENT_METHODS } from "../utils/salesLedger";
import { printWithTheme } from "../utils/typography";
import logo from "../assets/PdfImagetrs.png";
import SalesTable from "./SalesTable";
import { saleDateError } from "../utils/saleDate";
import "./DailyCashTab.css";

const TYPES = { sale: "Sales invoice", return: "Return", receipt: "Customer payment", income: "Other income", expense: "Expense" };
const amountText = amount => amount == null ? "—" : money(amount);
const invoiceReference = row => row.invoice === "Customer account" ? row.invoice : row.invoice ? `For ${row.invoice}` : "";
const metricValues = totals => [["Net sales", totals.netSales], ["Customer payments", totals.receipts],
  ["Other income", totals.income], ["Expenses", totals.expenses], ["Net receipts", totals.netReceipts], ["Sales less expenses", totals.salesLessExpenses]];

function JournalDialog({ title, onClose, children, busy = false }) {
  const dialog = useRef(null);
  useEffect(() => {
    const node = dialog.current;
    node.showModal();
    return () => node.close();
  }, []);
  return <dialog ref={dialog} className="ui-modal journal-dialog" aria-label={title}
    onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
    <div className="ui-heading-row"><h3>{title}</h3><button className="ui-secondary" type="button" onClick={onClose} disabled={busy}>Close</button></div>
    {children}
  </dialog>;
}

export default function DailyCashTab({ user }) {
  const allShowrooms = ["owner", "admin"].includes(String(user?.role || "").toLowerCase());
  const [date, setDate] = useState(businessDate);
  const [showroom, setShowroom] = useState(() => String(user?.showroom_id || ""));
  const scope = allShowrooms ? showroom : String(user?.showroom_id || "");
  const [showrooms, setShowrooms] = useState([]), [allLocations, setAllLocations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [source, setSource] = useState("");
  const [entry, setEntry] = useState(null);
  const [saleEntry, setSaleEntry] = useState(null);
  const [details, setDetails] = useState(null);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const generation = useRef(0), saving = useRef(false);
  const key = `${date}/${scope}`;
  const current = data?.key === key ? data : null;
  const locations = allShowrooms ? allLocations : showrooms;

  const reload = useCallback(async () => {
    const version = ++generation.current;
    setLoading(true); setError("");
    try {
      if (!allShowrooms && !scope) throw new Error("Assign a showroom to this employee to view the daily journal.");
      const next = await loadDailyJournal(supabase, date, scope);
      if (generation.current !== version) return;
      setData({ ...next, key: `${date}/${scope}` });
      setShowrooms(next.showrooms);
    } catch (err) {
      if (generation.current === version) { setError("Unable to load the daily journal: " + englishError(err.message)); setData(null); }
    } finally { if (generation.current === version) setLoading(false); }
  }, [date, scope, allShowrooms]);

  const cancelPending = useCallback(() => { generation.current++; }, []);
  useEffect(() => { reload(); return cancelPending; }, [reload, cancelPending]);
  useEffect(() => {
    const refresh = () => { if (!document.hidden && !saving.current && !entry && !saleEntry && !details) reload(); };
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => { window.removeEventListener("focus", refresh); window.clearInterval(timer); };
  }, [reload, entry, saleEntry, details]);

  // Preserve the full showroom selector when a manager narrows the report.
  useEffect(() => {
    if (!allShowrooms) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = [];
        for (let offset = 0; ; offset += 1000) {
          const { data: page, error: loadError } = await supabase.from("showroom").select("id,name").order("id").range(offset, offset + 999);
          if (loadError) throw loadError;
          rows.push(...(page || []));
          if (!page || page.length < 1000) break;
        }
        if (!cancelled) setAllLocations(rows);
      } catch (err) { if (!cancelled) setNotice("Unable to load showrooms: " + englishError(err.message)); }
    })();
    return () => { cancelled = true; };
  }, [allShowrooms]);

  function openEntry(type) {
    setFormError(""); setNotice("");
    setEntry({ type, incomeType: "other", date, showroom_id: scope, amount: "", method: "cash", voucher_no: "", description: "", cost_center: "" });
  }
  async function saveEntry(event) {
    event.preventDefault();
    if (saving.current) return;
    const amount = Number(entry.amount);
    if (!entry.showroom_id || (!allShowrooms && String(entry.showroom_id) !== scope) ||
        !locations.some(s => String(s.id) === String(entry.showroom_id))) { setFormError("Select a showroom."); return; }
    if (entry.type === "income" && entry.incomeType === "sale") {
      const dateError = saleDateError(entry.date);
      if (dateError) { setFormError(dateError); return; }
      setSaleEntry({ date: entry.date, showroom_id: Number(entry.showroom_id) });
      setEntry(null); return;
    }
    if (!Number.isFinite(amount) || amount <= 0 || !entry.description.trim()) { setFormError("Enter a positive amount and a description."); return; }
    saving.current = true; setBusy(true); setFormError("");
    try {
      const { error: saveError } = await supabase.from(entry.type === "expense" ? "daily_expense" : "daily_income_manual").insert([{
        showroom_id: Number(entry.showroom_id), date: journalDayBounds(entry.date).start,
        amount, method: entry.method, voucher_no: entry.voucher_no.trim() || null,
        description: entry.description.trim(), cost_center: entry.cost_center.trim() || null,
      }]);
      if (saveError) throw saveError;
      setEntry(null); setNotice(entry.type === "expense" ? "Expense saved." : "Income saved."); await reload();
    } catch (err) { setFormError("Unable to save the entry: " + englishError(err.message)); }
    finally { saving.current = false; setBusy(false); }
  }
  async function saleSaved(result) {
    setSaleEntry(null);
    setNotice(`Sale AH-${result.saleId} saved.`);
    const nextScope = scope ? String(result.showroomId) : "";
    setDate(result.date);
    if (allShowrooms) setShowroom(nextScope);
    if (date === result.date && scope === nextScope) await reload();
  }
  async function deleteEntry(row) {
    if (!row.manual || saving.current || !window.confirm("Delete this entry?")) return;
    saving.current = true; setBusy(true); setNotice("");
    try {
      const { error: deleteError } = await supabase.from(row.type === "expense" ? "daily_expense" : "daily_income_manual").delete().eq("id", row.id);
      if (deleteError) throw deleteError;
      setNotice("Entry deleted."); await reload();
    } catch (err) { setNotice("Unable to delete the entry: " + englishError(err.message)); }
    finally { saving.current = false; setBusy(false); }
  }
  function printJournal() {
    if (!current || loading || error) return;
    const win = window.open("", "_blank", "width=1100,height=800");
    if (!win) { setNotice("Allow pop-ups to print the daily journal."); return; }
    const e = escapeHtml;
    const location = scope ? locations.find(s => String(s.id) === scope)?.name || scope : "All showrooms";
    win.document.open();
    win.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Daily journal ${e(date)}</title><style>
      body{margin:24px;color:#514536}header{display:flex;gap:24px;align-items:center}header img{max-width:90px}
      table{width:100%;border-collapse:collapse;margin-top:24px}th,td{border:1px solid #d2cbc1;padding:8px;text-align:left;font-size:12px}
      .totals{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:24px 0}.totals p{margin:0}.totals strong{display:block}
      @page{size:A4 landscape;margin:12mm}tr{break-inside:avoid}
      </style></head><body><header><img src="${e(logo)}" alt="Abbas Halim logo"><div><h1>Abbas Halim · Daily journal</h1><p>${e(date)} · ${e(location)}</p></div></header>
      <div class="totals">${metricValues(current.totals).map(([label, value]) => `<p>${e(label)}<strong>${money(value)} EGP</strong></p>`).join("")}</div>
      <p>Sales: ${money(current.totals.sales)} EGP · Returns: ${money(current.totals.returns)} EGP</p>
      <table><thead><tr>${["Type", "Reference", "Customer", "Showroom", "Description", "Payment method", "Sales / returns EGP", "Money in / out EGP"].map(label => `<th>${label}</th>`).join("")}</tr></thead><tbody>
      ${journalPrintRows(current.rows).map(row => {
        const amounts = journalAmounts(row);
        const receipts = row.receipts || [];
        const references = receipts.map(receipt => `<br><small>${e(receipt.reference)}</small>`).join("");
        const methods = receipts.length ? receipts.map(receipt => `${e(PAYMENT_METHODS[receipt.method] || "—")}${receipts.length > 1 ? ` · ${money(receipt.amount)} EGP` : ""}`).join("<br>") : e(PAYMENT_METHODS[row.method] || "—");
        return `<tr><td>${e(TYPES[row.type])}</td><td>${e(row.reference)}${row.invoice ? `<br><small>${e(invoiceReference(row))}</small>` : ""}${references}</td><td>${e(row.customer)}</td><td>${e(row.showroom)}</td><td>${e(row.description)}</td><td>${methods}</td><td>${amountText(amounts.sales)}</td><td>${amountText(receipts.length ? row.receivedAmount : amounts.money)}</td></tr>`;
      }).join("") || '<tr><td colspan="8">No activity for this day.</td></tr>'}
      </tbody></table></body></html>`);
    win.document.close(); printWithTheme(win);
  }

  const rows = current?.rows.filter(row => !source || row.type === source) || [];
  const disabled = loading || busy || !!saleEntry || !current || !!error;
  return <section className="ui-legacy-page cash-book daily-journal" aria-label="Daily journal">
    <div className="ui-page-header"><h2>Daily journal</h2><div className="ui-actions">
      <button className="ui-primary" onClick={() => openEntry("expense")} disabled={disabled}>Add expense</button>
      <button className="ui-secondary" onClick={() => openEntry("income")} disabled={disabled}>Add income</button>
      <button className="ui-secondary" onClick={reload} disabled={loading || busy}>Refresh</button>
      <button className="ui-secondary" onClick={printJournal} disabled={disabled}>Print daily journal</button>
    </div></div>
    <div className="ui-grid ui-card cash-filters">
      <label className="ui-field">Date<input type="date" required value={date} onChange={event => { setDate(event.target.value); setNotice(""); }} disabled={busy} /></label>
      <label className="ui-field">Showroom<select aria-label="Showroom" value={scope} disabled={!allShowrooms || busy} onChange={event => { setShowroom(event.target.value); setNotice(""); }}>
        {allShowrooms && <option value="">All showrooms</option>}
        {!allShowrooms && !locations.length && <option value={scope}>{user?.showroom_name || "Assigned showroom"}</option>}
        {locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
      </select></label>
      <label className="ui-field">Activity<select aria-label="Activity" value={source} onChange={event => setSource(event.target.value)}><option value="">All activity</option>
        {Object.entries(TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select></label>
    </div>
    {notice && <p role="status">{notice}</p>}
    {error && <p role="alert">{error}</p>}
    {loading ? <p role="status">Loading daily journal…</p> : !error && current && <>
      <div className="ui-metrics">{metricValues(current.totals).map(([label, value]) => <div key={label}><span>{label}</span><strong>{money(value)} EGP</strong></div>)}</div>
      <div className="ui-heading-row"><h3>Daily activity</h3><span>Sales: {money(current.totals.sales)} EGP · Returns: {money(current.totals.returns)} EGP</span></div>
      <div className="ui-table-scroll"><table><thead><tr>{["Type", "Reference", "Customer", "Showroom", "Description", "Payment method", "Sales / returns (EGP)", "Money in / out (EGP)", "Actions"].map(label => <th key={label}>{label}</th>)}</tr></thead><tbody>
        {rows.map(row => {
          const amounts = journalAmounts(row);
          return <tr key={row.key}>
          <td>{TYPES[row.type]}</td><td>{row.reference}{row.invoice && <span className="journal-invoice-reference">{invoiceReference(row)}</span>}</td><td>{row.customer}</td><td>{row.showroom}</td><td>{row.description}</td>
          <td>{PAYMENT_METHODS[row.method] || "—"}</td><td className="journal-amount">{amountText(amounts.sales)}</td><td className="journal-amount">{amountText(amounts.money)}</td>
          <td><div className="ui-actions"><button className="ui-secondary" onClick={() => setDetails(row)} disabled={busy}>Details</button>
            {row.manual && <button className="ui-secondary" onClick={() => deleteEntry(row)} disabled={busy}>Delete</button>}
          </div></td>
        </tr>;})}
        {!rows.length && <tr><td colSpan={9}>{source ? "No entries match this activity." : "No activity for this day."}</td></tr>}
      </tbody></table></div>
    </>}
    {entry && <JournalDialog title={entry.type === "expense" ? "Add expense" : "Add income"} busy={busy} onClose={() => setEntry(null)}>
      <form className="ui-form ui-fields" onSubmit={saveEntry}>
        {entry.type === "income" && <label className="ui-full-width">Income type<select value={entry.incomeType} disabled={busy} onChange={event => { setEntry({ ...entry, incomeType: event.target.value }); setFormError(""); }}>
          <option value="other">Other income</option><option value="sale">Sale</option>
        </select></label>}
        <label>Date<input type="date" required value={entry.date} onChange={event => setEntry({ ...entry, date: event.target.value })} disabled={busy} /></label>
        <label>Showroom<select aria-label="Showroom" required value={entry.showroom_id} disabled={busy || !allShowrooms} onChange={event => setEntry({ ...entry, showroom_id: event.target.value })}>
          <option value="">Select a showroom</option>{locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
        </select></label>
        {!(entry.type === "income" && entry.incomeType === "sale") && <>
        <label>Amount (EGP)<input type="number" required min="0.01" step="0.01" value={entry.amount} disabled={busy} onChange={event => setEntry({ ...entry, amount: event.target.value })} /></label>
        <label>Payment method<select aria-label="Payment method" value={entry.method} disabled={busy} onChange={event => setEntry({ ...entry, method: event.target.value })}>
          {Object.entries(PAYMENT_METHODS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Voucher number<input value={entry.voucher_no} disabled={busy} onChange={event => setEntry({ ...entry, voucher_no: event.target.value })} /></label>
        <label>Cost center (optional)<input value={entry.cost_center} disabled={busy} onChange={event => setEntry({ ...entry, cost_center: event.target.value })} /></label>
        <label className="ui-full-width">Description<textarea required value={entry.description} disabled={busy} onChange={event => setEntry({ ...entry, description: event.target.value })} /></label>
        </>}
        {formError && <p role="alert" className="ui-full-width">{formError}</p>}
        <div className="ui-actions ui-form-actions ui-full-width"><button type="button" className="ui-secondary" disabled={busy} onClick={() => setEntry(null)}>Cancel</button>
          <button className="ui-primary" type="submit" disabled={busy}>{busy ? "Saving…" : entry.type === "expense" ? "Save expense" : entry.incomeType === "sale" ? "Continue to sale" : "Save income"}</button></div>
      </form>
    </JournalDialog>}
    {saleEntry && <SalesTable user={{ ...user, showroom_id: saleEntry.showroom_id }} formOnly initialDate={saleEntry.date}
      onSaleSaved={saleSaved} onCancel={() => setSaleEntry(null)} />}
    {details && <JournalDialog title={details.type === "sale" ? `Invoice ${details.reference}` : `${TYPES[details.type]} details`} onClose={() => setDetails(null)}>
      <dl className="journal-details">{[["Date", journalDate(details.date)], ["Reference", details.reference], ["Customer", details.customer], ["Showroom", details.showroom],
        ["Amount (EGP)", money(details.amount)], ["Description", details.description], ["Invoice", details.invoice], ["Payment reference", details.paymentReference],
        ["Payment method", PAYMENT_METHODS[details.method]], ["Cost center", details.costCenter]].filter(([, value]) => value).map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
      {details.type === "sale" && <><p>Before discount: {money(details.gross)} EGP · Discount: {money(details.discount)} EGP</p>
        <div className="ui-table-scroll"><table><thead><tr>{["Product", "Quantity", "Area (m²)", "Unit price (EGP)", "Total (EGP)"].map(label => <th key={label}>{label}</th>)}</tr></thead><tbody>
          {details.items.map(item => <tr key={item.id}><td>{item.code}</td><td>{item.slabs_sold}</td><td>{money(item.area_sold)}</td><td>{money(item.price_per_m2)}</td><td>{money(item.total_price)}</td></tr>)}
          {!details.items.length && <tr><td colSpan={5}>No line items.</td></tr>}
        </tbody></table></div></>}
    </JournalDialog>}
  </section>;
}
